import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Grid, Typography, Box, Checkbox, FormControlLabel, FormGroup,
    FormControl, FormLabel,
    Collapse, List, ListItem, ListItemIcon, Icon, Button, CircularProgress
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { AdminModuleService, GlobalService, RoleV2Service } from '@sdk-point/talisis';

import Select from '../../design-system/components/Form/Select/Select';
import Toast from '../../design-system/components/Toast/Toast';
import LoaderContent from '../../design-system/components/Loader/LoaderContent';
import TitleBanner from '../../design-system/components/Banners/TitleBanner';
import { commonModuleStyles } from '../../assets/styles/jss/pages/common/CommonModuleStyles';
import { SCOPE_TYPES, parseScopeType, serializeScopeType } from '../../utils/scopeType';

// ─── SDK Services ─────────────────────────────────────────────────────────────
// AdminModuleService (módulos + permisos) usa el entorno V2.
// GlobalService (unidades operativas / campus) usa el entorno base.
const AdminModuleServiceSDK = new AdminModuleService(
    process.env.REACT_APP_ENV_V2,
    process.env.REACT_APP_X_API_KEY_V2
);
const GlobalServiceSDK = new GlobalService(
    process.env.REACT_APP_ENV,
    process.env.REACT_APP_X_API_KEY
);
// RoleV2Service: fuente del selector de usuarios (GET /roles/{roleId}/users).
const RoleV2ServiceSDK = new RoleV2Service(
    process.env.REACT_APP_ENV_V2,
    process.env.REACT_APP_X_API_KEY_V2
);

// Rol "Admin Talisis": sus usuarios son los que pueden recibir permisos B2B.
const ROLE_ADMIN_TALISIS = 34;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Construye un mapa plano { module_id: boolean } desde el árbol que devuelve
 * AdminModuleService.getModulePermissions → modules[]. Usa `module_id` (string
 * jerárquico "1", "1.1") como clave, igual que el árbol de getModules.
 *
 * Procesa el response del GET getModulePermissions.
 */
function buildPermissionsMap(modules) {
    const map = {};
    if (!modules) return map;

    const walk = (nodes) => {
        nodes.forEach((node) => {
            const key = node.module_id ?? node.id;
            map[key] = !!node.enabled;
            if (node.children && node.children.length > 0) {
                walk(node.children);
            }
        });
    };
    walk(modules);
    return map;
}

/**
 * Get the list of child IDs (recursive) for a given module node.
 */
function getDescendantIds(node) {
    const ids = [];
    if (!node.children) return ids;
    node.children.forEach((child) => {
        ids.push(child.id);
        ids.push(...getDescendantIds(child));
    });
    return ids;
}

/**
 * Find a node in the module tree by its id.
 */
function findNodeById(tree, id) {
    for (const node of tree) {
        if (node.id === id) return node;
        if (node.children) {
            const found = findNodeById(node.children, id);
            if (found) return found;
        }
    }
    return null;
}

/**
 * Find the parent node of a given id in the tree.
 */
function findParentNode(tree, targetId, parent = null) {
    for (const node of tree) {
        if (node.id === targetId) return parent;
        if (node.children) {
            const found = findParentNode(node.children, targetId, node);
            if (found !== undefined) return found;
        }
    }
    return undefined;
}

/**
 * Compute parent state (checked / unchecked / indeterminate) given children.
 */
function computeParentState(parentNode, permissionsMap) {
    if (!parentNode || !parentNode.children || parentNode.children.length === 0) return null;
    const childStates = parentNode.children.map((c) => permissionsMap[c.id]);
    const allChecked = childStates.every(Boolean);
    const someChecked = childStates.some(Boolean);
    if (allChecked) return 'checked';
    if (someChecked) return 'indeterminate';
    return 'unchecked';
}

// ─── Component ───────────────────────────────────────────────────────────────
const Roles = () => {
    const classes = commonModuleStyles();
    const theme = useTheme();

    // person_id del admin logueado — se envía como `assigned_by` al guardar.
    const assignedBy = useSelector((state) => state.userReducer?.user?.person_id);

    // ─── State ───────────────────────────────────────────────────────────────
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [moduleTree, setModuleTree] = useState([]);
    const [modulePermissions, setModulePermissions] = useState({});
    // Tipos de alcance ACTIVOS. Antes era excluyente (un string "internal" |
    // "external"); ahora es un arreglo que puede contener uno o ambos tipos a la
    // vez. Default: solo interno.
    const [scopeTypes, setScopeTypes] = useState([SCOPE_TYPES.INTERNAL]);
    // Unidades operativas (empresas internas) con sus campus, desde GlobalService.
    const [operatingUnits, setOperatingUnits] = useState([]);
    // Sets de IDs seleccionados (no nombres): company_id y campus_id.
    const [internalCompanies, setInternalCompanies] = useState(new Set());
    const [internalCampuses, setInternalCampuses] = useState(new Set());
    const [externalCompanies, setExternalCompanies] = useState([]);
    const [allExternalCompanies, setAllExternalCompanies] = useState([]);
    const [originalState, setOriginalState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [expandedModules, setExpandedModules] = useState({});
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    // ─── API Loaders ─────────────────────────────────────────────────────────

    // Carga los usuarios del rol "Admin Talisis" vía
    // RoleV2Service.getUsersByRole(roleId) → GET /roles/{roleId}/users.
    // El response es { success, message, role_id, total, users: [{person_id, full_name}] }.
    // Se mapea a { id, label } para el selector.
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await RoleV2ServiceSDK.getUsersByRole(ROLE_ADMIN_TALISIS);
                const mapped = (response?.users || []).map((u) => ({
                    id: String(u.person_id),
                    label: u.full_name,
                }));
                setUsers(mapped);
            } catch (err) {
                console.error('[Roles] Error cargando usuarios por rol:', err?.response?.data || err.message || err);
                setUsers([]);
                setToast({ open: true, message: 'Error al cargar la lista de usuarios', severity: 'error' });
            }
        };
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Carga el árbol de módulos vía AdminModuleService.getModules().
    // Normaliza module_id -> id para que las helpers y el render (que keyan por
    // `id`) funcionen sin cambios. Conserva module_id para el payload de guardado.
    useEffect(() => {
        const fetchModules = async () => {
            try {
                const response = await AdminModuleServiceSDK.getModules();
                const raw = Array.isArray(response)
                    ? response
                    : (response?.data || response?.modules || []);

                const normalize = (nodes) => nodes.map((n) => ({
                    ...n,
                    id: n.module_id ?? n.id,       // clave estable para UI
                    module_id: n.module_id ?? n.id, // se preserva para el POST
                    children: Array.isArray(n.children) ? normalize(n.children) : [],
                }));
                const tree = normalize(raw);
                setModuleTree(tree);

                const expanded = {};
                tree.forEach((m) => { expanded[m.id] = true; });
                setExpandedModules(expanded);
            } catch (err) {
                console.error('[Roles] Error loading modules:', err?.response?.data || err.message || err);
                setToast({ open: true, message: 'Error al cargar módulos', severity: 'error' });
            }
        };
        fetchModules();
    }, []);

    // Carga las unidades operativas (empresas internas) y sus campus vía
    // GlobalService.getOperatingUnits(). Reemplaza INTERNAL_COMPANIES y
    // CAMPUSES_BY_COMPANY hardcodeados. Forma: [{ id, name, campus: [...] }].
    useEffect(() => {
        const fetchOperatingUnits = async () => {
            try {
                // Filtro explícito por internas (la doc del SDK acepta company_type).
                const response = await GlobalServiceSDK.getOperatingUnits({ company_type: 'internal' });
                const units = Array.isArray(response) ? response : (response?.data || []);
                setOperatingUnits(units);
            } catch (err) {
                console.error('[Roles] Error loading operating units:', err?.response?.data || err.message || err);
                setToast({ open: true, message: 'Error al cargar unidades operativas', severity: 'error' });
            }
        };
        fetchOperatingUnits();
    }, []);

    // Catálogo de empresas B2B externas, vía el mismo GlobalService.getOperatingUnits
    // con company_type: 'external'. Se mapea { id, name } -> { company_id, company_name }
    // (las externas no usan campus en la UI).
    useEffect(() => {
        const fetchExternal = async () => {
            try {
                const response = await GlobalServiceSDK.getOperatingUnits({ company_type: 'external' });
                const units = Array.isArray(response) ? response : (response?.data || []);
                const mapped = units.map((u) => ({ company_id: u.id, company_name: u.name }));
                setAllExternalCompanies(mapped);
            } catch (err) {
                console.error('[Roles] Error loading external companies:', err?.response?.data || err.message || err);
                setToast({ open: true, message: 'Error al cargar empresas externas', severity: 'error' });
            }
        };
        fetchExternal();
    }, []);

    // Carga los permisos del usuario seleccionado vía
    // AdminModuleService.getModulePermissions(personId).
    const loadUserPermissions = useCallback(async (personId) => {
        if (!personId) return;
        setLoading(true);

        try {
            // GET real: la Lambda devuelve { success, message, scope, modules } con
            // el scope anidado por tipo (scope[scope_type]) y los módulos con enabled.
            // Para un usuario nuevo, trae el árbol completo con enabled=false y
            // scope internal vacío (la Lambda resuelve ese caso).
            const data = await AdminModuleServiceSDK.getModulePermissions(personId);

            // Mapa de permisos por module_id.
            const permMap = buildPermissionsMap(data.modules);
            setModulePermissions(permMap);

            // Scope: el contrato soporta uno o AMBOS tipos a la vez. `scope_type`
            // llega como string CSV ("internal", "external" o "internal, external").
            // Se parsea a un arreglo de tipos activos y se pueblan AMBAS cajas de
            // forma independiente (cada caja trae sus propias companies, sin importar
            // la otra). Si por compatibilidad no viniera scope_type pero sí datos en
            // una caja, se infiere el tipo de la presencia de companies.
            const scope = data.scope || {};
            let types = parseScopeType(scope.scope_type);
            if (types.length === 0) {
                // Inferencia de respaldo: deducir tipos activos por las cajas con datos.
                if ((scope.internal?.companies || []).length > 0) types.push(SCOPE_TYPES.INTERNAL);
                if ((scope.external?.companies || []).length > 0) types.push(SCOPE_TYPES.EXTERNAL);
                if (types.length === 0) types = [SCOPE_TYPES.INTERNAL]; // default seguro
            }
            setScopeTypes(types);

            // Internal: Sets de IDs. Se lee SIEMPRE la caja internal (no depende de
            // la caja external). company_id a nivel company; campus_id dentro de cada
            // company (campo campuse_id por el contrato actual del backend).
            const intCompIds = new Set();
            const intCampusIds = new Set();
            (scope.internal?.companies || []).forEach((c) => {
                intCompIds.add(c.company_id);
                (c.campuses || []).forEach((cam) => intCampusIds.add(cam.campuse_id ?? cam.campus_id ?? cam.id));
            });
            setInternalCompanies(intCompIds);
            setInternalCampuses(intCampusIds);

            // External: lista de { company_id, company_name }, leída SIEMPRE de la
            // caja external (independiente de internal).
            const extComp = (scope.external?.companies || [])
                .map((c) => ({ company_id: c.company_id, company_name: c.company_name }));
            setExternalCompanies(extComp);

            // Snapshot para "Cancelar".
            setOriginalState({
                modulePermissions: { ...permMap },
                scopeTypes: [...types],
                internalCompanies: new Set(intCompIds),
                internalCampuses: new Set(intCampusIds),
                externalCompanies: [...extComp],
            });
        } catch (err) {
            console.error('[Roles] Error loading user permissions:', err?.response?.data || err.message || err);
            setToast({ open: true, message: 'Error al cargar permisos del usuario', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    // ─── Event Handlers ──────────────────────────────────────────────────────

    const handleUserChange = (e) => {
        const value = e.target.value;
        setSelectedUserId(value);
        if (value) {
            loadUserPermissions(value);
        }
    };

    // Module checkbox toggle with parent/child propagation
    const handleModuleToggle = (nodeId, checked) => {
        setModulePermissions((prev) => {
            const next = { ...prev };
            const node = findNodeById(moduleTree, nodeId);
            if (!node) return prev;

            // Set this node
            next[nodeId] = checked;

            // Propagate to all descendants
            const descendantIds = getDescendantIds(node);
            descendantIds.forEach((id) => { next[id] = checked; });

            // Update ancestors upward
            let current = nodeId;
            let parent = findParentNode(moduleTree, current);
            while (parent) {
                const state = computeParentState(parent, next);
                next[parent.id] = state === 'checked';
                current = parent.id;
                parent = findParentNode(moduleTree, current);
            }

            return next;
        });
    };

    // Determine indeterminate state for a parent node
    const isIndeterminate = (node) => {
        if (!node.children || node.children.length === 0) return false;
        const childStates = node.children.map((c) => modulePermissions[c.id]);
        const allChecked = childStates.every(Boolean);
        const noneChecked = childStates.every((s) => !s);
        // Also check deeper descendants for indeterminate
        if (allChecked || noneChecked) {
            // Check if any child itself is indeterminate
            return node.children.some((c) => isIndeterminate(c));
        }
        return true;
    };

    const toggleExpand = (nodeId) => {
        setExpandedModules((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }));
    };

    // Scope handlers. El tipo de alcance ya no es excluyente: se puede marcar
    // interno, externo o ambos. Este toggle agrega/quita un tipo del arreglo
    // `scopeTypes`. Al desmarcar un tipo se limpian las selecciones de su caja
    // para no enviar empresas de un alcance que el usuario desactivó.
    const handleScopeTypeToggle = (type) => {
        setScopeTypes((prev) => {
            const isActive = prev.includes(type);
            if (isActive) {
                if (type === SCOPE_TYPES.INTERNAL) {
                    setInternalCompanies(new Set());
                    setInternalCampuses(new Set());
                } else if (type === SCOPE_TYPES.EXTERNAL) {
                    setExternalCompanies([]);
                }
                return prev.filter((t) => t !== type);
            }
            return [...prev, type];
        });
    };

    // Recibe el company_id (id de la unidad operativa). Al desmarcar una empresa,
    // también se quitan sus campus seleccionados.
    const handleInternalCompanyToggle = (companyId) => {
        setInternalCompanies((prev) => {
            const next = new Set(prev);
            if (next.has(companyId)) {
                next.delete(companyId);
                const unit = operatingUnits.find((u) => u.id === companyId);
                const campusIds = (unit?.campus || []).map((c) => c.id);
                setInternalCampuses((prevCampuses) => {
                    const nextCampuses = new Set(prevCampuses);
                    campusIds.forEach((id) => nextCampuses.delete(id));
                    return nextCampuses;
                });
            } else {
                next.add(companyId);
            }
            return next;
        });
    };

    const handleCampusToggle = (campus) => {
        setInternalCampuses((prev) => {
            const next = new Set(prev);
            if (next.has(campus)) {
                next.delete(campus);
            } else {
                next.add(campus);
            }
            return next;
        });
    };

    const handleExternalCompanyToggle = (company) => {
        setExternalCompanies((prev) => {
            const exists = prev.find((c) => c.company_id === company.company_id);
            if (exists) {
                return prev.filter((c) => c.company_id !== company.company_id);
            }
            return [...prev, { company_id: company.company_id, company_name: company.company_name }];
        });
    };

    // Save — construye el payload exacto que valida la Lambda POST.
    const handleSave = async () => {
        if (!selectedUserId) return;
        if (!assignedBy) {
            setToast({ open: true, message: 'No se pudo identificar al usuario que asigna (assigned_by)', severity: 'error' });
            return;
        }
        setSaving(true);
        try {
            // Árbol de módulos: module_id + enabled BOOLEANO estricto (la Lambda
            // rechaza "partial"). El estado indeterminado se resuelve a true/false.
            const buildModulesPayload = (nodes) => nodes.map((node) => ({
                module_id: node.module_id ?? node.id,
                enabled: !!modulePermissions[node.module_id ?? node.id],
                ...(node.children && node.children.length > 0
                    ? { children: buildModulesPayload(node.children) }
                    : {}),
            }));

            // Companies internas como objetos { company_id, company_name, campuses }.
            // Solo los campus seleccionados que pertenezcan a cada company.
            const internalPayloadCompanies = operatingUnits
                .filter((u) => internalCompanies.has(u.id))
                .map((u) => ({
                    company_id: u.id,
                    company_name: u.name,
                    campuses: (u.campus || [])
                        .filter((c) => internalCampuses.has(c.id))
                        .map((c) => ({ campuse_id: c.id, campuese_name: c.name })),
                }));

            // scope_type se envía como string CSV con los tipos ACTIVOS
            // ("internal", "external" o "internal, external"). Cada caja lleva sus
            // empresas solo si su tipo está activo; la caja de un tipo inactivo va
            // vacía. Esto permite enviar AMBOS alcances a la vez.
            const internalActive = scopeTypes.includes(SCOPE_TYPES.INTERNAL);
            const externalActive = scopeTypes.includes(SCOPE_TYPES.EXTERNAL);

            const payload = {
                assigned_by: Number(assignedBy),
                modules: buildModulesPayload(moduleTree),
                scope: {
                    scope_type: serializeScopeType(scopeTypes),
                    internal: { companies: internalActive ? internalPayloadCompanies : [] },
                    external: {
                        companies: externalActive
                            ? externalCompanies.map((c) => ({ company_id: c.company_id, company_name: c.company_name }))
                            : [],
                    },
                },
            };

            await AdminModuleServiceSDK.postModulePermissions(Number(selectedUserId), payload);

            // Update original state to current (new baseline)
            setOriginalState({
                modulePermissions: { ...modulePermissions },
                scopeTypes: [...scopeTypes],
                internalCompanies: new Set(internalCompanies),
                internalCampuses: new Set(internalCampuses),
                externalCompanies: [...externalCompanies],
            });

            setToast({ open: true, message: 'Permisos guardados correctamente', severity: 'success' });

            // Recargar la página para traer los valores recién guardados desde el
            // backend y refrescar el menú lateral. Se usa window.location.reload()
            // (recarga DURA) en lugar de refrescar en caliente a propósito:
            //   - Mantiene la URL actual (/admin/administracion/roles), así que el
            //     usuario se queda en esta pantalla. El early-return por
            //     `permissionsLoading` en Routes.jsx muestra un loader durante el
            //     arranque y el guard del Sidebar respeta la URL actual, evitando que
            //     lo expulse a otro módulo.
            //   - NO reconstruye el árbol de permisos en caliente, por lo que NO
            //     dispara la cascada de redirecciones Routes↔Sidebar que causaba el
            //     bucle de navegación (React error #185) al llamar loadPermissions.
            // El pequeño delay deja ver el toast de confirmación antes de recargar.
            setTimeout(() => {
                window.location.reload();
            }, 800);
        } catch (err) {
            console.error('Error saving permissions:', err);
            setToast({ open: true, message: 'Error al guardar permisos', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    // Cancel — restore from originalState
    const handleCancel = () => {
        if (!originalState) return;
        setModulePermissions({ ...originalState.modulePermissions });
        setScopeTypes([...originalState.scopeTypes]);
        setInternalCompanies(new Set(originalState.internalCompanies));
        setInternalCampuses(new Set(originalState.internalCampuses));
        setExternalCompanies([...originalState.externalCompanies]);
    };

    // ─── Render: Module Tree ─────────────────────────────────────────────────
    const renderModuleNode = (node, depth = 0) => {
        if (!node) return null;
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = expandedModules[node.id] !== false;
        const isChecked = !!modulePermissions[node.id];
        const indeterminate = hasChildren && isIndeterminate(node);

        return (
            <React.Fragment key={node.id}>
                <ListItem
                    className={depth > 0 ? classes.nestedItem : classes.moduleItem}
                    style={{
                        paddingLeft: theme.spacing(2 + depth * 2),
                        paddingTop: theme.spacing(0.5),
                        paddingBottom: theme.spacing(0.5),
                    }}
                >
                    <ListItemIcon style={{ minWidth: 40, display: 'flex', alignItems: 'center' }}>
                        {hasChildren ? (
                            <Icon
                                className={clsx(
                                    classes.expandIcon,
                                    isExpanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'
                                )}
                                onClick={() => toggleExpand(node.id)}
                                style={{
                                    color: isChecked ? theme.palette.colors?.electricBlue?.[300] || '#1976d2' : theme.palette.colors?.gray?.[500] || '#666',
                                    fontSize: 16,
                                    cursor: 'pointer',
                                }}
                            />
                        ) : (
                            <Icon
                                className="ri-arrow-right-s-line"
                                style={{
                                    fontSize: 16,
                                    marginRight: theme.spacing(1),
                                    color: theme.palette.colors?.gray?.[500] || '#666',
                                    visibility: 'hidden',
                                }}
                            />
                        )}
                    </ListItemIcon>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isChecked}
                                indeterminate={indeterminate && !isChecked}
                                onChange={(e) => handleModuleToggle(node.id, e.target.checked)}
                                color="primary"
                            />
                        }
                        label={node.name || node.id}
                        className={classes.checkboxLabel}
                        style={{ marginLeft: 0 }}
                    />
                </ListItem>
                {hasChildren && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {node.children.map((child) => renderModuleNode(child, depth + 1))}
                        </List>
                    </Collapse>
                )}
            </React.Fragment>
        );
    };

    // ─── Campus disponibles según las empresas internas seleccionadas ─────────
    // Devuelve objetos { id, name } de las unidades operativas marcadas.
    const availableCampuses = React.useMemo(() => {
        const campuses = [];
        const seen = new Set();
        operatingUnits.forEach((unit) => {
            if (!internalCompanies.has(unit.id)) return;
            (unit.campus || []).forEach((c) => {
                if (!seen.has(c.id)) {
                    seen.add(c.id);
                    campuses.push({ id: c.id, name: c.name });
                }
            });
        });
        return campuses;
    }, [internalCompanies, operatingUnits]);

    // ─── Access Guard ───────────────────────────────────────────────────────
    // Note: role-based access (super_user) was removed. Access to this screen is
    // now governed by the module permissions assigned to the user, not a role flag.

    // ─── Render ──────────────────────────────────────────────────────────────
    return (
        <LoaderContent loading={loading}>
            <Grid container spacing={2}>
                {/* Banner */}
                <Grid item xs={12}>
                    <TitleBanner
                        title="Accesos del Sistema"
                        desc="Administra los permisos de módulos y el ámbito de datos de cada usuario. Selecciona un usuario para configurar su acceso."
                    />
                </Grid>

                {/* Contenido */}
                <Grid item xs={12}>
                    <Card>
                        <Box p={2}>
                            {/* Título de sección */}
                            <Typography variant="h6" className={classes.sectionTitle}>
                                Configuración de Accesos
                            </Typography>

                            {/* User Selector */}
                            <Box mb={4} mt={2}>
                                <Typography variant="body2" style={{ marginBottom: theme.spacing(1), fontWeight: 500, color: theme.palette.colors?.darkBlack?.[500] || '#333' }}>
                                    Seleccionar Usuario
                                </Typography>
                                <Box maxWidth={300}>
                                    <Select
                                        label=""
                                        value={selectedUserId}
                                        onChange={handleUserChange}
                                        items={users}
                                        name="user"
                                    />
                                </Box>
                            </Box>

                            {selectedUserId && (
                                <Grid container spacing={3}>
                                    {/* Module Permissions Panel */}
                                    <Grid item xs={12} md={6}>
                                        <Box className={classes.section}>
                                            <Typography variant="h6" className={classes.sectionTitle} style={{ marginBottom: theme.spacing(2) }}>
                                                Permisos de Módulos
                                            </Typography>
                                            <List style={{ paddingTop: 0 }}>
                                                {moduleTree.map((node) => renderModuleNode(node, 0))}
                                            </List>
                                        </Box>
                                    </Grid>

                                    {/* Data Scope Panel */}
                                    <Grid item xs={12} md={6}>
                                        <Box className={classes.section}>
                                            <Typography variant="h6" className={classes.sectionTitle}>
                                                Ámbito de Datos
                                            </Typography>
                                            <Box className={classes.dataScopeContainer}>
                                                {/* Scope Type Checkboxes — ya no es excluyente: se
                                                    puede marcar Interno, Externo o ambos a la vez. */}
                                                <FormControl component="fieldset">
                                                    <FormLabel component="legend" style={{ fontWeight: 600, marginBottom: theme.spacing(1) }}>
                                                        Tipo de Alcance
                                                    </FormLabel>
                                                    <FormGroup row>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={scopeTypes.includes(SCOPE_TYPES.INTERNAL)}
                                                                    onChange={() => handleScopeTypeToggle(SCOPE_TYPES.INTERNAL)}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label="Interno"
                                                        />
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={scopeTypes.includes(SCOPE_TYPES.EXTERNAL)}
                                                                    onChange={() => handleScopeTypeToggle(SCOPE_TYPES.EXTERNAL)}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label="Externo"
                                                        />
                                                    </FormGroup>
                                                </FormControl>

                                                {/* Internal Scope */}
                                                {scopeTypes.includes(SCOPE_TYPES.INTERNAL) && (
                                                    <>
                                                        <Box className={classes.dataScopeSection}>
                                                            <Typography variant="subtitle1" style={{ marginBottom: 16, fontWeight: 600 }}>
                                                                Empresas Internas
                                                            </Typography>
                                                            {operatingUnits.length === 0 ? (
                                                                <Typography variant="body2" color="textSecondary">
                                                                    Cargando empresas internas...
                                                                </Typography>
                                                            ) : (
                                                                <FormGroup>
                                                                    {operatingUnits.map((unit) => (
                                                                        <FormControlLabel
                                                                            key={unit.id}
                                                                            control={
                                                                                <Checkbox
                                                                                    checked={internalCompanies.has(unit.id)}
                                                                                    onChange={() => handleInternalCompanyToggle(unit.id)}
                                                                                    color="primary"
                                                                                />
                                                                            }
                                                                            label={unit.name}
                                                                        />
                                                                    ))}
                                                                </FormGroup>
                                                            )}
                                                        </Box>

                                                        {/* Campus Panel */}
                                                        <Box className={classes.dataScopeSection}>
                                                            <Typography variant="subtitle1" style={{ marginBottom: 16, fontWeight: 600 }}>
                                                                Campus Permitidos
                                                            </Typography>
                                                            {internalCompanies.size === 0 ? (
                                                                <Typography variant="body2" color="textSecondary">
                                                                    Selecciona una empresa para ver sus campus
                                                                </Typography>
                                                            ) : (
                                                                <FormGroup>
                                                                    {availableCampuses.map((campus) => (
                                                                        <FormControlLabel
                                                                            key={campus.id}
                                                                            control={
                                                                                <Checkbox
                                                                                    checked={internalCampuses.has(campus.id)}
                                                                                    onChange={() => handleCampusToggle(campus.id)}
                                                                                    color="primary"
                                                                                />
                                                                            }
                                                                            label={campus.name}
                                                                        />
                                                                    ))}
                                                                </FormGroup>
                                                            )}
                                                        </Box>
                                                    </>
                                                )}

                                                {/* External Scope */}
                                                {scopeTypes.includes(SCOPE_TYPES.EXTERNAL) && (
                                                    <Box className={classes.dataScopeSection}>
                                                        <Typography variant="subtitle1" style={{ marginBottom: 16, fontWeight: 600 }}>
                                                            Empresas B2B Externas
                                                        </Typography>
                                                        {allExternalCompanies.length === 0 ? (
                                                            <Typography variant="body2" color="textSecondary">
                                                                Cargando catálogo de empresas...
                                                            </Typography>
                                                        ) : (
                                                            <FormGroup>
                                                                {allExternalCompanies.map((company) => (
                                                                    <FormControlLabel
                                                                        key={company.company_id}
                                                                        control={
                                                                            <Checkbox
                                                                                checked={externalCompanies.some((c) => c.company_id === company.company_id)}
                                                                                onChange={() => handleExternalCompanyToggle(company)}
                                                                                color="primary"
                                                                            />
                                                                        }
                                                                        label={company.company_name}
                                                                    />
                                                                ))}
                                                            </FormGroup>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            )}

                            {/* Action Buttons */}
                            {selectedUserId && (
                                <Box className={classes.actionButtons}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleCancel}
                                        className={classes.cancelButton}
                                        disabled={saving}
                                        style={{ textTransform: 'none' }}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={handleSave}
                                        disabled={saving}
                                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Icon className="ri-save-line" />}
                                        style={{ textTransform: 'none' }}
                                    >
                                        {saving ? 'Guardando...' : 'Guardar Cambios'}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Card>
                </Grid>
            </Grid>

            {/* Toast Notifications */}
            <Toast
                open={toast.open}
                severity={toast.severity}
                message={toast.message}
                onFinished={() => setToast({ ...toast, open: false })}
            />
        </LoaderContent>
    );
};

export default Roles;
